import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Shield, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/Authcontext';
import { useTheme } from '../contexts/Themecontext';
import { authAPI } from '../services/api';

const AdminSignup = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // If no email in URL, redirect to request access
    if (!searchParams.get('email')) {
      navigate('/admin/request-access');
    }
  }, [searchParams, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.adminSignup({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      const { user, access_token, refresh_token } = response;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      login(user, access_token);
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Admin signup error:', err);
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        card: 'bg-white',
        border: 'border-gray-200',
        text: 'text-gray-900',
        textMuted: 'text-gray-600',
        input: 'bg-white border-gray-300 text-gray-900 focus:border-purple-500',
        button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        card: 'bg-slate-800',
        border: 'border-slate-700',
        text: 'text-white',
        textMuted: 'text-gray-400',
        input: 'bg-slate-900 border-slate-700 text-white focus:border-purple-500',
        button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-purple-950 to-pink-950',
      card: 'bg-slate-900/50 backdrop-blur-xl',
      border: 'border-purple-500/30',
      text: 'text-white',
      textMuted: 'text-gray-300',
      input: 'bg-slate-900/50 border-purple-500/30 text-white focus:border-purple-400',
      button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
    };
  };

  const classes = getThemeClasses();

  return (
    <div className={`min-h-screen ${classes.bg} flex items-center justify-center px-4 py-12 pt-20 sm:px-6 lg:px-8`}>
      <div className="relative max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 p-1">
              <div className="w-full h-full bg-slate-900 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>
          <h2 className={`text-3xl font-bold ${classes.text}`}>Create Admin Account</h2>
          <p className={`mt-2 ${classes.textMuted}`}>Complete your admin registration</p>
        </div>

        <div className={`${classes.card} border ${classes.border} rounded-2xl shadow-2xl p-8`}>
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors`}
                    placeholder="John"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className={`block w-full px-3 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors`}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`w-5 h-5 ${classes.textMuted}`} />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  readOnly
                  className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors bg-gray-100 dark:bg-gray-800`}
                  placeholder="admin@glyqix.com"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`w-5 h-5 ${classes.textMuted}`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`block w-full pl-10 pr-10 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className={`w-5 h-5 ${classes.textMuted}`} />
                  ) : (
                    <Eye className={`w-5 h-5 ${classes.textMuted}`} />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`w-5 h-5 ${classes.textMuted}`} />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className={`block w-full pl-10 pr-10 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className={`w-5 h-5 ${classes.textMuted}`} />
                  ) : (
                    <Eye className={`w-5 h-5 ${classes.textMuted}`} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <UserPlus size={20} />
                  Create Admin Account
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className={`text-sm ${classes.textMuted}`}>
              Already have an account?{' '}
              <Link to="/admin/login" className="font-medium text-purple-400 hover:text-purple-300 transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center">
          <p className={`text-xs ${classes.textMuted}`}>
            <Shield className="w-3 h-3 inline mr-1" />
            Secure admin registration
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSignup;